import { useCallback, useEffect } from 'react';
import type {
  DefaultValues,
  FieldValues,
  Path,
  PathValue,
  UseFormProps,
} from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

interface UseFilterFormOptions<TData extends FieldValues>
  extends UseFormProps<TData> {
  isOpen?: boolean;
  onApply: () => void;
  defaultValues?: DefaultValues<TData>;
}

function isDropdownFilter(filter: string) {
  return ['userTypes', 'roles'].includes(filter);
}

/**
 * Enhanced filter form hook that handles:
 * - Form state management with react-hook-form
 * - Bidirectional URL searchParams synchronization
 * - Apply action: updates URL with filter values
 * - Clear action: resets form and removes filter params from URL
 */
export const useFilterForm = <TData extends FieldValues>({
  isOpen,
  onApply,
  ...formOptions
}: UseFilterFormOptions<TData>) => {
  const isFilterKey = useCallback(
    (key: string | number | symbol): key is Path<TData> =>
      formOptions?.defaultValues ? key in formOptions.defaultValues : false,
    [formOptions?.defaultValues],
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const filterForm = useForm<TData>(formOptions);
  const { reset, setValue, getValues } = filterForm;

  const handleClear = useCallback(() => {
    reset(formOptions?.defaultValues);

    // Remove all filter params from URL
    if (
      formOptions?.defaultValues &&
      typeof formOptions.defaultValues === 'object'
    ) {
      const newParams = new URLSearchParams(searchParams);

      Object.keys(formOptions.defaultValues).forEach((key) => {
        newParams.delete(key);
      });
      setSearchParams(newParams);
    }
  }, [reset, formOptions?.defaultValues, searchParams, setSearchParams]);

  const handleApply = useCallback(
    (filterData: TData) => {
      const newParams = new URLSearchParams(searchParams);

      for (const [key, filter] of Object.entries(filterData)) {
        if (Array.isArray(filter) && filter.length && !filter.includes('all')) {
          newParams.set(
            key,
            filter
              .map((item) => (typeof item === 'object' ? item.value : item))
              .sort()
              .join(','),
          );
        } else if (filter && !Array.isArray(filter) && filter !== 'all') {
          newParams.set(key, filter);
        } else if (
          (filter === 'all' && searchParams.has(key)) ||
          (Array.isArray(filter) &&
            (filter.length === 0 || filter.includes('all')))
        ) {
          newParams.delete(key);
        }
      }

      setSearchParams(newParams);
      onApply();
    },
    [onApply, searchParams, setSearchParams],
  );

  // Sync URL params to form when drawer opens
  useEffect(() => {
    if (isOpen) {
      for (const [key, value] of searchParams) {
        if (isFilterKey(key)) {
          if (Array.isArray(getValues(key))) {
            setValue(
              key,
              value
                .split(',')
                .map((val) =>
                  isDropdownFilter(key) ? val : { value: val },
                ) as PathValue<TData, Path<TData>>,
            );
          } else {
            setValue(key, value as PathValue<TData, Path<TData>>);
          }
        }
      }
    }
  }, [isOpen, searchParams, setValue, getValues, isFilterKey]);

  return {
    filterForm,
    handleClear,
    handleApply,
  };
};
